import React from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Selection,
  SortDescriptor,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { capitalize } from "@/utils/utils";

import { LuChevronDown } from "react-icons/lu";

// Définir un type générique <T> pour le tableau
interface ColumnType {
  name: string;
  uid: string;
  sortable?: boolean;
  style?: React.CSSProperties;
}

interface ClassNames {
  wrapper?: string[];
  table?: string[];
  th?: string[];
  td?: string[];
  label?: string[];
}

interface DataTableProps<T> {
  columns: ColumnType[];
  data: T[];
  renderCell: (item: T, columnKey: React.Key) => React.ReactNode;
  initialSortDescriptor?: SortDescriptor;
  initialVisibleColumns?: string[];
  topContent?: React.ReactNode;
  itemsPerPageOptions?: number[];
  keyExtractor: (item: T) => string;
  classNames?: ClassNames;
  selectionMode?: "none" | "single" | "multiple";
  onSelectionChange?: (selectedItems: T[]) => void;
  hideColumnSelector?: boolean;
}

function DataTable<T>(props: Readonly<DataTableProps<T>>) {
  const {
    columns,
    data,
    renderCell,
    initialSortDescriptor,
    initialVisibleColumns,
    topContent,
    itemsPerPageOptions = [15, 50],
    keyExtractor,
    classNames,
    selectionMode = "single",
    onSelectionChange,
    hideColumnSelector = false,
  } = props;
  const { t } = useTranslation();

  const [selectedKeys, setSelectedKeys] = React.useState<Selection>(
    new Set([]),
  );
  const [visibleColumns, setVisibleColumns] = React.useState<Selection>(
    new Set(initialVisibleColumns || columns.map((c) => c.uid)),
  );
  const [rowsPerPage, setRowsPerPage] = React.useState(itemsPerPageOptions[0]);
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor>(
    initialSortDescriptor || { column: columns[0].uid, direction: "ascending" },
  );
  const [page, setPage] = React.useState(1);

  const headerColumns = React.useMemo(() => {
    if (visibleColumns === "all") return columns;
    return columns.filter((column) =>
      Array.from(visibleColumns).includes(column.uid),
    );
  }, [visibleColumns, columns]);

  const sortedData = React.useMemo(() => {
    return [...data].sort((a: T, b: T) => {
      const first = a[sortDescriptor.column as keyof T];
      const second = b[sortDescriptor.column as keyof T];

      let cmp = 0;
      if (typeof first === "number" && typeof second === "number") {
        cmp = first - second;
      } else if (typeof first === "string" && typeof second === "string") {
        cmp = first.localeCompare(second);
      }

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor, data]);

  const pages = Math.ceil(sortedData.length / rowsPerPage);

  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [page, sortedData, rowsPerPage]);

  const onRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleSelectionChange = (newSelection: Selection) => {
    console.log(newSelection);
    setSelectedKeys(newSelection);
    if (onSelectionChange) {
      const selectedItems =
        newSelection === "all"
          ? data
          : data.filter((item) => (newSelection as Set<string>).has(keyExtractor(item)));
      onSelectionChange(selectedItems);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          {topContent}
          {!hideColumnSelector && (
            <div className="flex gap-3">
              <div className="flex gap-3">
                <Dropdown>
                  <DropdownTrigger className="hidden sm:flex">
                    <Button
                      endContent={<LuChevronDown className="text-small" />}
                      size="sm"
                      variant="flat"
                      radius="full"
                      className="h-9 bg-white text-vistagrey text-small"
                    >
                      {t("columns")}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    disallowEmptySelection
                    aria-label={t("table_columns")}
                    closeOnSelect={false}
                    selectedKeys={visibleColumns}
                    selectionMode="multiple"
                    onSelectionChange={setVisibleColumns}
                  >
                    {columns.map((column) => (
                      <DropdownItem
                        key={column.uid}
                        textValue={column.name}
                        className="capitalize"
                      >
                        {capitalize(t(column.name))}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          )}
        </div>
      </div>
      <Table
        aria-label="Data Table"
        removeWrapper
        selectedKeys={selectedKeys}
        sortDescriptor={sortDescriptor}
        onSelectionChange={handleSelectionChange}
        onSortChange={setSortDescriptor}
        classNames={classNames}
        selectionMode={selectionMode}
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              allowsSorting={column.sortable}
              align={column.uid === "actions" ? "center" : "start"}
              style={column.style}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody items={items}>
          {(item: T) => (
            <TableRow key={keyExtractor(item)}>
              {(columnKey) => (
                <TableCell
                  style={columns.find((col) => col.uid === columnKey)?.style}
                >
                  {renderCell(item, columnKey)}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="py-2 px-2 flex justify-between items-center">
        <span className="text-default-400 text-small">
          {t("total")}: {sortedData.length}
        </span>
      </div>
      <div className="py-2 px-2 flex justify-between items-center">
        <Pagination
          showControls
          isCompact
          page={page}
          total={pages}
          variant="light"
          onChange={setPage}
        />
        <label className="flex items-center text-default-400 text-small">
          <span>{t("item_per_page")}</span>
          <select
            className="bg-transparent outline-none text-default-400 text-small"
            onChange={onRowsPerPageChange}
            value={rowsPerPage}
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>
    </>
  );
}

export default DataTable;
